import React, { useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

const paginationStyles = {
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    padding: "16px 0",
    gap: "8px",
    flexWrap: "wrap"
  },
  link: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "6px 8px"
  },
  linkDisabled: {
    color: "#9ca3af",
    cursor: "not-allowed",
    opacity: 0.7
  },
  pageNum: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    minWidth: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    border: "none",
    backgroundColor: "transparent",
    borderRadius: "6px"
  },
  pageNumActive: {
    backgroundColor: "#e5e7eb",
    color: "#374151",
    fontWeight: 500
  },
  ellipsis: {
    color: "#6b7280",
    fontSize: "0.875rem",
    padding: "0 4px",
    minWidth: "24px",
    textAlign: "center"
  }
};

function CustomPagination({ rowCount, rowsPerPage, currentPage, onChangePage, totalRows }) {
  const total = rowCount ?? totalRows ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i);
      if (totalPages > 5) pages.push("...");
      if (totalPages > 5) pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      if (totalPages > 5) pages.push("...");
      for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div style={paginationStyles.wrapper}>
      <button
        type="button"
        aria-label="Previous page"
        style={{
          ...paginationStyles.link,
          ...(!canPrev ? paginationStyles.linkDisabled : {})
        }}
        onClick={() => canPrev && onChangePage(currentPage - 1)}
        disabled={!canPrev}
      >
        <ChevronLeft size={18} />
        Previous
      </button>
      {pageNumbers.map((p, i) =>
        p === "..." ? (
          <span key={`ellipsis-${i}`} style={paginationStyles.ellipsis}>
            ...
          </span>
        ) : (
          <button
            key={p}
            type="button"
            style={{
              ...paginationStyles.pageNum,
              ...(p === currentPage ? paginationStyles.pageNumActive : {})
            }}
            onClick={() => onChangePage(p)}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        aria-label="Next page"
        style={{
          ...paginationStyles.link,
          ...(!canNext ? paginationStyles.linkDisabled : {})
        }}
        onClick={() => canNext && onChangePage(currentPage + 1)}
        disabled={!canNext}
      >
        Next
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

const ActivityLog = ({ activityData }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activityFilter, setActivityFilter] = useState("All Activities");

  const activityTypes = useMemo(() => {
    const types = new Set();
    (Array.isArray(activityData) ? activityData : []).forEach((row) => {
      if (row && row.activity) {
        types.add(String(row.activity));
      }
    });
    return Array.from(types);
  }, [activityData]);

  const activityColumns = [
    {
      name: "Timestamp",
      selector: row => row.timestamp,
      sortable: true,
      width: "200px"
    },
    {
      name: "Activity",
      selector: row => row.activity,
      sortable: true,
      wrap: true
    },
    {
      name: "User/Source",
      cell: row => {
        if (!row.user || !row.user.name || !row.user.email) {
          return <div></div>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {row.user.avatar && (
              <img 
                src={row.user.avatar} 
                alt={row.user.name}
                style={{ width: "32px", height: "32px", borderRadius: "50%" }}
              />
            )}
            <div>
              <div style={{ fontWeight: 500 }}>{row.user.name}</div>
              <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>{row.user.email}</div>
            </div>
          </div>
        );
      },
      sortable: false,
      width: "260px"
    },
    {
      name: "Details",
      selector: row => row.details,
      sortable: true,
      wrap: true
    }
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#002347",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px"
      }
    },
    pagination: {
      style: {
        justifyContent: "center",
        flexWrap: "wrap"
      }
    }
  };

  const filteredActivityData = useMemo(() => {
    let filtered = Array.isArray(activityData) ? activityData : [];

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter((row) => {
        const fields = [
          row.timestamp,
          row.activity,
          row.details,
          row.user?.name,
          row.user?.email
        ];
        return fields.some(
          (field) =>
            field &&
            String(field)
              .toLowerCase()
              .includes(query)
        );
      });
    }

    if (activityFilter && activityFilter !== "All Activities") {
      filtered = filtered.filter(
        (row) => String(row.activity || "") === String(activityFilter)
      );
    }

    return filtered;
  }, [activityData, searchQuery, activityFilter]);

  return (
    <div className="activity-log-section">
      <h2 className="activity-log-title">Activity Log</h2>
      <div className="activity-log-controls">
        <div className="search-container">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-controls">
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            className="filter-select"
          >
            <option value="All Activities">All Activities</option>
            {activityTypes.map((type) => (
              <option key={type} value={type}>
                {String(type).replace(/_/g, " ")}
              </option>
            ))}
          </select>
          {/* <button className="filter-button">
            <Filter size={18} />
            Filters
          </button> */}
        </div>
      </div>
      <div className="table-container">
        <DataTable
          columns={activityColumns}
          data={filteredActivityData}
          customStyles={customStyles}
          striped
          highlightOnHover
          noHeader={false}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10]}
          paginationComponent={CustomPagination}
        />
      </div>
    </div>
  );
};

export default ActivityLog;

